import React from 'react';
import { Icon, IconName, Tooltip } from '@usealloy/component-library';
import type { FraudSignalIndicator } from '~/entities/FeatureImportanceWidget/types';
import {
  StyledIndicatorsList,
  StyledIndicatorRow,
  StyledIndicatorLabel,
  StyledTooltipContent,
  StyledTooltipDirection,
  StyledTooltipDescription,
} from './FraudSignalIndicators.styles';

const SCORE_THRESHOLD = 0.25;

const getIndicatorIcon = (score: number) => {
  if (score >= SCORE_THRESHOLD) {
    return <Icon icon={IconName.GaugeHigh} color="var(--cl-red-500)" />;
  }
  return <Icon icon={IconName.GaugeLow} color="var(--cl-green-500)" />;
};

const getIndicatorTooltipContent = (
  indicator: FraudSignalIndicator,
  indicatesIncrease: boolean,
) => (
  <StyledTooltipContent>
    <StyledTooltipDirection $indicatesIncrease={indicatesIncrease}>
      <Icon
        icon={indicatesIncrease ? IconName.GaugeHigh : IconName.GaugeLow}
        color={indicatesIncrease ? 'var(--cl-red-500)' : 'var(--cl-green-500)'}
      />
      <span>{indicatesIncrease ? 'Increases score' : 'Decreases score'}</span>
    </StyledTooltipDirection>
    <StyledTooltipDescription>{indicator.description}</StyledTooltipDescription>
  </StyledTooltipContent>
);

interface FraudSignalIndicatorsProps {
  indicators: FraudSignalIndicator[];
  score: number;
  twoColumns?: boolean;
  selectedIndicatorName?: string | null;
  onIndicatorClick?: (indicator: FraudSignalIndicator) => void;
}

export const FraudSignalIndicators = ({
  indicators,
  score,
  twoColumns = false,
  selectedIndicatorName,
  onIndicatorClick,
}: FraudSignalIndicatorsProps) => {
  const indicatorIcon = getIndicatorIcon(score);
  const isInteractive = !!onIndicatorClick;
  const indicatesIncrease = score >= SCORE_THRESHOLD;

  return (
    <StyledIndicatorsList $twoColumns={twoColumns}>
      {indicators.map(indicator => {
        const isSelected = selectedIndicatorName === indicator.name;
        const showTooltip = !isInteractive && !!indicator.description;

        const indicatorRow = (
          <StyledIndicatorRow
            $isInteractive={isInteractive}
            $isSelected={isSelected}
            $twoColumns={twoColumns}
            role={isInteractive ? 'button' : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={() => onIndicatorClick?.(indicator)}
            onKeyDown={e => {
              if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onIndicatorClick?.(indicator);
              }
            }}
            title={twoColumns && !showTooltip ? indicator.name : undefined}
          >
            {indicatorIcon}
            <StyledIndicatorLabel $twoColumns={twoColumns}>
              {indicator.name}
            </StyledIndicatorLabel>
          </StyledIndicatorRow>
        );

        if (showTooltip) {
          return (
            <Tooltip
              key={indicator.name}
              content={getIndicatorTooltipContent(indicator, indicatesIncrease)}
              side="top"
            >
              {indicatorRow}
            </Tooltip>
          );
        }

        return (
          <React.Fragment key={indicator.name}>
            {indicatorRow}
          </React.Fragment>
        );
      })}
    </StyledIndicatorsList>
  );
};
